import { CALL_STATUS_LABELS, type AnalysisStatus, type CallStatus, type Direction } from '@lia/contracts';
import { CallIncoming01Icon, CallOutgoing01Icon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDateTime, formatDuration } from '../../lib/format.js';
import { StatusBadge } from '../primitives/badge.js';
import { Card } from '../primitives/card.js';
import { Spinner } from '../primitives/spinner.js';
import { ANALYSIS_STATUS_LABELS, ANALYSIS_STATUS_TONE, CALL_STATUS_TONE } from './status.js';

export interface CallCardProps {
  direction: Direction;
  status: CallStatus;
  startedAt: string;
  timezone: string;
  /** null = unknown (shown as "—", never 0). */
  durationSeconds: number | null;
  analysisStatus: AnalysisStatus;
  summary?: string | null;
  contactName?: string;
  /** e.g. "Agente IA · Línea Torre Alameda" or advisor name. */
  handledBy?: string;
  /** Actions: Ver transcripción, Escuchar grabación… */
  actions?: React.ReactNode;
  className?: string;
}

export const CallCard = React.forwardRef<HTMLDivElement, CallCardProps>(function CallCard(
  { direction, status, startedAt, timezone, durationSeconds, analysisStatus, summary, contactName, handledBy, actions, className },
  ref,
) {
  const dirIcon = direction === 'inbound' ? CallIncoming01Icon : CallOutgoing01Icon;
  const dirLabel = direction === 'inbound' ? 'Llamada entrante' : 'Llamada saliente';
  return (
    <Card ref={ref} as="article" className={cn('grid gap-3 p-4', className)}>
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-pill bg-surface-subtle text-text-secondary">
          <Icon icon={dirIcon} className="size-[18px]" />
        </span>
        <div className="grid min-w-0 flex-1 gap-0.5">
          <p className="text-body font-semibold text-text-primary">
            {dirLabel}
            {contactName ? ` · ${contactName}` : ''}
          </p>
          <p className="ef-tabular text-meta text-text-secondary">
            {formatDateTime(startedAt, timezone)} · Duración {formatDuration(durationSeconds)}
            {handledBy ? ` · ${handledBy}` : ''}
          </p>
        </div>
        <StatusBadge tone={CALL_STATUS_TONE[status]}>{CALL_STATUS_LABELS[status]}</StatusBadge>
      </div>
      <div className="grid gap-1.5 rounded-control bg-surface-subtle p-3">
        <div className="flex items-center gap-2">
          <span className="text-meta font-medium text-text-secondary">Resumen</span>
          <StatusBadge tone={ANALYSIS_STATUS_TONE[analysisStatus]} icon={analysisStatus === 'processing' ? <Spinner size="sm" /> : undefined}>
            {ANALYSIS_STATUS_LABELS[analysisStatus]}
          </StatusBadge>
        </div>
        {summary ? (
          <p className="text-body text-text-primary">{summary}</p>
        ) : (
          <p className="text-body text-text-secondary">
            {analysisStatus === 'processing'
              ? 'El resumen estará disponible cuando termine el análisis.'
              : analysisStatus === 'failed'
                ? 'No se pudo analizar la llamada. La grabación y la transcripción siguen disponibles si existen.'
                : 'Sin resumen para esta llamada.'}
          </p>
        )}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </Card>
  );
});
